import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@ui-kit/components/Button';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/analytics';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import Send2Icon from '@/components/icons/Send2Icon';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import useCountdown from '@/components/OTPVerification/hooks/useCountdown';
import OTPForm from '@/components/OTPVerification/OTPForm';
import { AnalyticsEvents } from '@/constants/analytics';
import { ErrorIds } from '@/constants/ApiResponse';
import useModal from '@/hooks/useModal';
import useRequestEmailVerificationCode from '@/hooks/useRequestEmailVerificationCode';
import { formatElapsedDuration } from '@/localization/duration/duration';
import { parseApiError } from '@/utils/error';

type Payload = { email: string; resendAfter: string };

const EmailVerificationOtp = ({
  payload: { email, resendAfter },
  closeModal,
  confirmAction = closeModal,
}: ModalProps<Payload>) => {
  const { t } = useTranslation(['Modal', 'FormErrors']);
  const { openModal } = useModal();
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);
  const [resendExpiration, setResendExpiration] = useState(() => new Date(resendAfter));
  const [emailSent, setEmailSent] = useState(false);

  const resetFormFields = useCallback(() => {
    setFormKey(prevKey => prevKey + 1);
  }, []);

  const {
    mutate: verifyCode,
    isPending: isVerifyPending,
    error: verifyError,
    reset: resetVerifyMutation,
  } = useMutation({
    mutationFn: API.user.verifyEmailVerificationCode,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.creditActions });
      openModal('SE.EMAIL_VERIFICATION_SUCCESS', { confirmAction });
    },
    onError: resetFormFields,
  });

  const {
    requestEmailVerificationCode: resendCode,
    isPending: isResendPending,
    errorMessage: resendErrorMessage,
    reset: resetResendMutation,
  } = useRequestEmailVerificationCode({
    onSuccess: ({ resendAfter: nextResendAfter }) => {
      setResendExpiration(new Date(nextResendAfter));
      resetFormFields();
      resetVerifyMutation();
      setEmailSent(true);
    },
    onAlreadyVerified: confirmAction,
  });

  const handleVerify = useCallback(
    (verificationCode: string) => {
      resetVerifyMutation();
      resetResendMutation();
      setEmailSent(false);
      verifyCode(verificationCode);
    },
    [resetVerifyMutation, resetResendMutation, verifyCode],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      if (newCode.length === 0) {
        return;
      }

      setEmailSent(false);
      // Only reset when an error is showing — unconditional reset re-renders forever
      // because OTPForm's onCodeChange effect depends on this callback identity.
      if (verifyError) {
        resetVerifyMutation();
      }
    },
    [verifyError, resetVerifyMutation],
  );

  const resendCooldownSeconds = useCountdown(resendExpiration);
  const isResendDisabled = resendCooldownSeconds > 0 || isResendPending || isVerifyPending;

  const { errorId, metadata } = verifyError ? parseApiError(verifyError) : {};
  const triesLeft = metadata?.codeTriesLeft;
  const isWrongCodeWithTries = errorId === ErrorIds.WRONG_CODE && typeof triesLeft === 'number';
  const isTerminalVerifyError =
    errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED || errorId === ErrorIds.CODE_EXPIRED;
  const canEnterCode = !isTerminalVerifyError;
  const isInvalid = isWrongCodeWithTries || isTerminalVerifyError;

  let inlineErrorMessage: string | undefined;
  if (resendErrorMessage) {
    inlineErrorMessage = resendErrorMessage;
  } else if (verifyError) {
    if (isWrongCodeWithTries) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.wrongCode', { count: triesLeft });
    } else if (errorId === ErrorIds.CODE_EXPIRED) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.codeExpired');
    } else if (errorId === ErrorIds.MAX_VERIFICATION_TRIES_REACHED) {
      inlineErrorMessage = t('Modal:EmailVerificationOtp.maxTriesReached');
    } else {
      inlineErrorMessage = t('FormErrors:somethingWentWrong');
    }
  }

  const resendLabel =
    resendCooldownSeconds > 0
      ? t('Modal:EmailVerificationOtp.resendWithCountdown', {
          duration: formatElapsedDuration(resendCooldownSeconds),
        })
      : t('Modal:EmailVerificationOtp.resend');

  return (
    <Modal title="">
      <div className="-mt-2 mb-6 flex flex-col items-center text-center">
        <Send2Icon className="text-icon-brand mb-4 size-12" aria-hidden="true" />
        <h2 className="text-heading mb-2 lg:text-center">{t('Modal:EmailVerificationOtp.title')}</h2>
        <p className="text-text-subtle">
          {t('Modal:EmailVerificationOtp.subtitle')}
          <br />
          <span data-private>{email}</span>
        </p>
      </div>
      <div className="flex flex-col gap-4">
        <OTPForm
          key={formKey}
          onSubmit={handleVerify}
          onCodeChange={handleCodeChange}
          isInvalid={isInvalid}
          isDisabled={!canEnterCode || isVerifyPending || isResendPending}
        />
        {emailSent && !inlineErrorMessage ? (
          <p className="text-caption text-text-success text-center">
            {t('Modal:EmailVerificationOtp.codeSent')}
          </p>
        ) : null}
        {inlineErrorMessage ? (
          <p role="alert" className="text-caption text-text-error text-center">
            {inlineErrorMessage}
          </p>
        ) : null}
        <ModalActions>
          <Button
            className="tabular-nums"
            type="button"
            variant="tertiary"
            disabled={isResendDisabled}
            loading={isResendPending}
            onClick={() => {
              trackEvent(AnalyticsEvents.SE.EMAIL_VERIFICATION_OTP_RESEND);
              setEmailSent(false);
              resendCode(email);
            }}
          >
            {resendLabel}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
};

export default EmailVerificationOtp;
